/**
 * Global chat notifications
 * Subscribes to DM realtime events anywhere in the authenticated app so that:
 *  - the conversation list refreshes even when the user is not on /chats
 *  - the user sees a Sonner toast when a new message arrives outside the
 *    conversation that message belongs to
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useRealtimeEvent } from "@/contexts/RealtimeContext"
import { useAuthStore } from "@/features/auth/store/authStore"

interface DmMessagePayload {
  id: string
  conversationId: string
  senderId: string
  sender?: { id: string; name: string | null; username: string | null } | null
  content: string
  createdAt: string
  isDeleted?: boolean
}

export function useGlobalChatNotifications(): void {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const currentUserId = useAuthStore(s => s.user?.id)

  useRealtimeEvent(
    "dm:new_message",
    useCallback(
      (payload: unknown) => {
        const msg = payload as DmMessagePayload
        if (!msg?.conversationId) return

        queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
        queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
        queryClient.invalidateQueries({ queryKey: ["dm-conversation", msg.conversationId] })

        // Don't toast for the sender's own echo or for deleted messages.
        if (msg.senderId && msg.senderId === currentUserId) return
        if (msg.isDeleted) return

        // Suppress the toast while the user is already viewing the thread.
        const activePath = `/chats/dm/${msg.conversationId}`
        if (location.pathname === activePath) return

        const senderName = msg.sender?.name || msg.sender?.username || "Someone"
        toast.info(`New message from ${senderName}`, {
          description: msg.content?.slice(0, 80),
          action: {
            label: "Open",
            onClick: () => navigate(activePath),
          },
        })
      },
      [queryClient, location.pathname, currentUserId, navigate],
    ),
  )
}
